import type { InternalBundlerCapabilitiesOptions, PackageJson } from '@ms-cloudpack/common-types';
import { parseRequestInfo } from '@ms-cloudpack/import-map';
import { findFileInPackage, findResolveMapEntry } from '@ms-cloudpack/package-utilities';
import { makeUrl, normalizeRelativePath } from '@ms-cloudpack/path-string-parsing';
import type { PartialContext } from '../types/Context.js';
import type { OverrideOptions } from '../types/OverrideOptions.js';
import { ensureGeneratedSettingsForPackage, readGeneratedConfig } from '@ms-cloudpack/config';

/**
 * Given a package name, import path, and optional issuerUrl, returns a boolean indicating if the given
 * override is valid.
 */
export async function validateOverride(
  options: Omit<OverrideOptions, 'version' | 'initiatedBy'>,
  context: PartialContext<'packages', 'config' | 'resolveMap'>,
): Promise<boolean> {
  const { packageName, importPath, issuerUrl } = options;
  const { session, packages } = context;
  const { config } = session;
  const { appPath } = config;

  let requiredBy: PackageJson | undefined = undefined;

  // Import paths with trailing slashes are not supported by import maps
  // in browser as they interpret the trailing slash as a wildcard partial match.
  if (importPath.endsWith('/')) {
    return false;
  }

  if (issuerUrl) {
    const { packageName: name, version } = parseRequestInfo(makeUrl(issuerUrl).pathname);
    requiredBy = { name, version };
  }

  const entry = findResolveMapEntry({
    packageName,
    resolveMap: session.resolveMap,
    definition: requiredBy,
  });

  if (!entry) {
    return false;
  }

  const packagePath = entry.path;

  const bundlerCapabilities = ((await packages.get(packagePath)).cloudpack?.bundlerCapabilities ||
    {}) as InternalBundlerCapabilitiesOptions;
  const resolveWebExtensions =
    // eslint-disable-next-line etc/no-deprecated
    bundlerCapabilities?.['resolve-web-extensions'] ?? !!config.features?.resolveWebExtensions;

  const { filePath } = await findFileInPackage(
    {
      packagePath,
      filePath: importPath,
      resolveWebExtensions,
    },
    { packages },
  );

  if (filePath) {
    return true;
  }

  // Parse the generated config if it exists.
  const generatedConfig = await readGeneratedConfig(appPath);

  // Ensure packageSettings exists.
  const generatedPackageSettings = await ensureGeneratedSettingsForPackage(
    { packagePath },
    { config: { generated: generatedConfig }, packages },
  );

  return (
    !!generatedPackageSettings?.exports && generatedPackageSettings.exports[normalizeRelativePath(importPath)] === null
  );
}
